import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import type { SidebarItemDetail } from '@verantos-dev/shared-components';

import type { SidebarConfig } from './types';

export interface UseSidebarOptions {
  /**
   * Configuration for sidebar behavior (icons, URLs, handlers, etc.)
   */
  config: SidebarConfig;
  /**
   * Array of allowed apps/features from useAllowedApps or similar
   */
  allowedApps?: Array<{
    appId: string;
    appName: string;
    features?: Array<{ featureId: string }>;
    additionalItems?: Array<{ featureId: string }>;
    sectionTitleId?: string;
  }>;
  /**
   * When true, the sidebar section-title labels (e.g. "Annotation platform")
   * are omitted. Nav items and dividers are kept. Defaults to falsy, so apps
   * that don't set it are unaffected.
   */
  // TODO: cleanup with cp_app_header_rename ff
  hideSectionTitles?: boolean;
}

export const useSidebar = ({ config, allowedApps, hideSectionTitles }: UseSidebarOptions) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // Get app-specific context from the config
  const context = config.getContext();

  // Check for first pathname
  const path = window.location.pathname;
  const segments = path.replace(/\/+$/, '').split('/').filter(Boolean);

  // get top level sidebar app
  const sidebarItems = useMemo(() => {
    if (!allowedApps || allowedApps.length === 0) {
      return [];
    }

    let items: SidebarItemDetail[] = [];

    // Track which section titles have been added to avoid duplicates
    const addedSectionIds = new Set<string>();

    items = allowedApps.reduce((_sidebarItems, app) => {
      const matchPatterns = config.getMatchPatterns(app.appId);
      const isSelected = matchPatterns.includes(segments[segments.length - 1]);

      const nextSidebarItem: SidebarItemDetail = {
        id: app.appId,
        label: intl.formatMessage({ id: `sidebar.${app.appName}` }),
        icon: config.getIcon(app.appId),
        selected: isSelected,
        disabled: false,
        isSelectable: true,
        href: config.getUrl(app.appId, context),
        onClick: config.getClickHandler(app.appId, navigate, context),
        secondaryLabel: '',
        divider: false,
      };

      let hasActiveSubItemForApp = false;
      const subItems: SidebarItemDetail[] | undefined = app.features?.map((feature) => {
        const featureMatchPatterns = config.getMatchPatterns(feature.featureId);
        const isSelectable = featureMatchPatterns.includes(segments[segments.length - 1]);
        hasActiveSubItemForApp = hasActiveSubItemForApp || isSelectable;
        return {
          id: feature.featureId as string,
          label: intl.formatMessage({ id: `sidebar.${app.appName}.${feature.featureId}` }),
          icon: config.getIcon(feature.featureId),
          selected: isSelectable,
          disabled: false,
          isSelectable: true,
          href: config.getUrl(feature.featureId, context),
          onClick: config.getClickHandler(feature.featureId, navigate, context),
          divider: false,
          secondaryLabel: '',
          isSubPage: true, // Mark feature items as sub-pages for indentation
        };
      });

      const additionalItems: SidebarItemDetail[] | undefined = app.additionalItems?.map(
        (feature) => {
          const featureMatchPatterns = config.getMatchPatterns(feature.featureId);
          const isSelectable = featureMatchPatterns.includes(segments[segments.length - 1]);
          return {
            id: feature.featureId as string,
            label: intl.formatMessage({ id: `sidebar.${feature.featureId}` }),
            icon: config.getIcon(feature.featureId),
            selected: isSelectable,
            disabled: false,
            isSelectable: true,
            href: config.getUrl(feature.featureId, context),
            onClick: config.getClickHandler(feature.featureId, navigate, context),
            divider: false,
            secondaryLabel: '',
          };
        }
      );

      // Only add section title if it hasn't been added yet for this section
      // TODO: cleanup with cp_app_header_rename ff
      const shouldAddSectionTitle =
        app?.sectionTitleId && !hideSectionTitles && !addedSectionIds.has(app.sectionTitleId);
      if (shouldAddSectionTitle && app.sectionTitleId) {
        addedSectionIds.add(app.sectionTitleId);
      }

      const sectionTitle: SidebarItemDetail | undefined =
        shouldAddSectionTitle && app.sectionTitleId
          ? {
              icon: 'noIcon',
              id: app.sectionTitleId + '-section',
              isSelectable: false,
              label: '',
              secondaryLabel: intl.formatMessage({
                id: `sidebar.sectionTitle.${app.sectionTitleId}`,
              }),
              sectionLabel: true,
            }
          : undefined;

      if (hasActiveSubItemForApp) {
        // Show subItems if they exist (not just when one is active)
        // if (subItems && subItems.length > 0) {
        const dividerConfig = config.dividerConfig || {};
        const noDividerForActiveSubItems = dividerConfig.noDividerForActiveSubItems || [];
        const shouldAddDivider = !noDividerForActiveSubItems.includes(app.appId);

        if (shouldAddDivider) {
          if (subItems && subItems.length > 0) {
            subItems[subItems.length - 1].divider = additionalItems?.length === 0;
          }
          if (additionalItems && additionalItems.length > 0) {
            additionalItems[additionalItems.length - 1].divider = true;
          } else if (subItems && subItems.length > 0) {
            subItems[subItems.length - 1].divider = true;
          } else {
            nextSidebarItem.divider = true;
          }
        }

        return sectionTitle
          ? [
              ..._sidebarItems,
              sectionTitle,
              nextSidebarItem,
              ...(additionalItems || []),
              ...(subItems || []),
            ]
          : [..._sidebarItems, nextSidebarItem, ...(additionalItems || []), ...(subItems || [])];
      }

      if (additionalItems && additionalItems.length > 0) {
        additionalItems[additionalItems.length - 1].divider = true;
      } else {
        const dividerConfig = config.dividerConfig || {};
        const noDividerForSingleItems = dividerConfig.noDividerForSingleItems || [];
        // Add divider for all single items except those in the no-divider list
        nextSidebarItem.divider = !noDividerForSingleItems.includes(app.appId);
      }
      return sectionTitle
        ? [..._sidebarItems, sectionTitle, nextSidebarItem, ...(additionalItems || [])]
        : [..._sidebarItems, nextSidebarItem, ...(additionalItems || [])];
    }, [] as SidebarItemDetail[]);

    // Always add sidebar toggle as the first item (before everything else)
    const toggleItem: SidebarItemDetail = {
      id: 'sidebar-toggle',
      icon: isSidebarOpen ? 'leftChevron' : 'rightChevron',
      label: '',
      selected: false,
      disabled: false,
      isSelectable: true,
      onClick: toggleSidebar,
      divider: false,
    };

    // Add additional items from config (e.g., Research Assistant)
    // These should be inserted before section titles and app items
    if (config.additionalItems && config.additionalItems.length > 0) {
      const sortedAdditionalItems = [...config.additionalItems].sort(
        (a, b) => a.position - b.position
      );

      // Separate items by position
      const itemsBeforeApps: SidebarItemDetail[] = [];
      const itemsAfterApps: SidebarItemDetail[] = [];

      for (const additionalItem of sortedAdditionalItems) {
        const item = additionalItem.getItem();
        if (additionalItem.position === -1) {
          // Add to end (after all app items)
          itemsAfterApps.push(item);
        } else if (additionalItem.position <= 1) {
          // Insert right after toggle (position 0 = toggle, position 1 = after toggle)
          itemsBeforeApps.push(item);
        } else {
          // For positions > 1, insert at specific position in app items array
          // This is more complex - for now, add to itemsBeforeApps
          // TODO: Handle positions > 1 by inserting into items array at correct position
          itemsBeforeApps.push(item);
        }
      }

      // Build final array: toggle, items before apps, app items, items after apps
      return [toggleItem, ...itemsBeforeApps, ...items, ...itemsAfterApps];
    }

    // If no additional items, just add toggle at the beginning
    return [toggleItem, ...items];
  }, [allowedApps, config, intl, navigate, context, segments, isSidebarOpen, hideSectionTitles]);

  return { sidebarItems, isSidebarOpen };
};
